import { useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import { useInbox, useOpenServiceRequests, useOpenTasks } from "./data";
import { SignInPage } from "./pages/SignInPage";
import { TodayPage } from "./pages/TodayPage";
import { InboxPage } from "./pages/InboxPage";
import { PeoplePage } from "./pages/PeoplePage";
import { DonorsPage } from "./pages/DonorsPage";
import { TriagePage } from "./pages/TriagePage";
import { PersonPage } from "./pages/PersonPage";
import { DuplicatesPage } from "./pages/DuplicatesPage";
import { DatesPage } from "./pages/DatesPage";
import { ImportPage } from "./pages/ImportPage";
import { EventsPage } from "./pages/EventsPage";
import { EventPage } from "./pages/EventPage";
import { RollCallPage } from "./pages/RollCallPage";
import { MorePage } from "./pages/MorePage";
import { SettingsPage } from "./pages/SettingsPage";
import { MessagesPage } from "./pages/MessagesPage";
import { TasksPage } from "./pages/TasksPage";
import { QuickAdd } from "./ui/QuickAdd";
import {
  IcCalendar,
  IcCheck,
  IcClock,
  IcHome,
  IcInbox,
  IcMore,
  IcPeople,
  IcPlus,
  IcSettings,
  IcTent,
  IcWallet,
  IcWhatsapp,
} from "./ui/icons";

function useInboxBadgeCount(): number {
  const inbox = useInbox();
  const tasks = useOpenTasks();
  const serviceRequests = useOpenServiceRequests();
  return inbox.data.length + tasks.items.length + serviceRequests.items.length;
}

export function App() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [quickAdd, setQuickAdd] = useState(false);

  if (status === "loading") {
    return (
      <div className="signin-wrap">
        <div className="signin">
          <div className="signin-mark">חב״ד</div>
          <p style={{ marginTop: 18, marginBottom: 0 }}>טוען…</p>
        </div>
      </div>
    );
  }

  if (status !== "ready") return <SignInPage />;

  return (
    <div className="app">
      <Sidebar onQuickAdd={() => setQuickAdd(true)} />

      <Routes>
        <Route path="/" element={<TodayPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/people" element={<PeoplePage />} />
        <Route path="/people/:personId" element={<PersonPage />} />
        <Route path="/donors" element={<DonorsPage />} />
        <Route path="/triage" element={<TriagePage />} />
        <Route path="/duplicates" element={<DuplicatesPage />} />
        <Route path="/dates" element={<DatesPage />} />
        <Route path="/rollcall" element={<RollCallPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:eventId" element={<EventPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="*" element={<TodayPage />} />
      </Routes>

      <BottomNav onQuickAdd={() => setQuickAdd(true)} />
      {quickAdd && (
        <QuickAdd
          onClose={() => setQuickAdd(false)}
          onOpenPerson={(personId) => {
            setQuickAdd(false);
            navigate(`/people/${personId}`);
          }}
        />
      )}
    </div>
  );
}

/** Visible only from 1024px up — the desktop dashboard rail, on the right in RTL. */
function Sidebar({ onQuickAdd }: { onQuickAdd: () => void }) {
  const { user, role, signOutNow } = useAuth();
  const inboxCount = useInboxBadgeCount();

  const roleLabel: Record<string, string> = {
    owner: "בעלים",
    admin: "מנהל",
    staff: "צוות",
    viewer: "צפייה בלבד",
  };

  return (
    <aside className="sidebar">
      <div className="side-brand">
        <div className="side-mark">חב״ד</div>
        <div>
          <b>בית חב״ד הפארק</b>
          <small>{roleLabel[role ?? ""] ?? ""}</small>
        </div>
      </div>

      <button className="side-add" onClick={onQuickAdd}>
        <IcPlus size={19} /> הוספה מהירה
      </button>

      <div className="side-group">עבודה שוטפת</div>
      <NavLink to="/" end className={sideCls}>
        <IcHome size={19} /> היום
      </NavLink>
      <NavLink to="/inbox" className={sideCls}>
        <IcInbox size={19} /> אינבוקס
        {inboxCount > 0 && <span className="side-count">{inboxCount}</span>}
      </NavLink>
      <NavLink to="/people" className={sideCls}>
        <IcPeople size={19} /> אנשי קשר
      </NavLink>
      <NavLink to="/donors" className={sideCls}>
        <IcWallet size={19} /> תורמים ותרומות
      </NavLink>
      <NavLink to="/tasks" className={sideCls}>
        <IcClock size={19} /> משימות ותזכורות
      </NavLink>

      <div className="side-group">קהילה ואירועים</div>
      <NavLink to="/events" className={sideCls}>
        <IcTent size={19} /> אירועים
      </NavLink>
      <NavLink to="/rollcall" className={sideCls}>
        <IcCheck size={19} /> סימון נוכחות
      </NavLink>
      <NavLink to="/dates" className={sideCls}>
        <IcCalendar size={19} /> תאריכים ויארצייטים
      </NavLink>
      <NavLink to="/messages" className={sideCls}>
        <IcWhatsapp size={19} /> נוסחי הודעות
      </NavLink>

      <div className="side-group">ניהול</div>
      <NavLink to="/settings" className={sideCls}>
        <IcSettings size={19} /> הגדרות ונתונים
      </NavLink>

      <div className="side-foot">
        {user?.email}
        <br />
        <button
          onClick={signOutNow}
          style={{ textDecoration: "underline", padding: "6px 0 0", color: "var(--white)" }}
        >
          יציאה מהחשבון
        </button>
      </div>
    </aside>
  );
}

/** Visible below 1024px only. */
function BottomNav({ onQuickAdd }: { onQuickAdd: () => void }) {
  const inboxCount = useInboxBadgeCount();

  return (
    <nav className="nav">
      <NavLink to="/" end className={navCls}>
        <span className="nav-ico">
          <IcHome size={21} />
        </span>
        היום
      </NavLink>

      <NavLink to="/people" className={navCls}>
        <span className="nav-ico">
          <IcPeople size={21} />
        </span>
        אנשי קשר
      </NavLink>

      <button className="fab" aria-label="הוספה מהירה" onClick={onQuickAdd}>
        <IcPlus />
      </button>

      <NavLink to="/inbox" className={navCls}>
        <span className="nav-ico">
          <IcInbox size={21} />
          {inboxCount > 0 && (
            <span
              className="badge-dot badge-dot-nav"
            >
              {inboxCount}
            </span>
          )}
        </span>
        אינבוקס
      </NavLink>

      <NavLink to="/more" className={navCls}>
        <span className="nav-ico">
          <IcMore size={21} />
        </span>
        תפריט
      </NavLink>
    </nav>
  );
}

function navCls({ isActive }: { isActive: boolean }) {
  return isActive ? "nav-item on" : "nav-item";
}

function sideCls({ isActive }: { isActive: boolean }) {
  return isActive ? "side-link on" : "side-link";
}
